import PropTypes from 'prop-types';
import React, { useEffect, useContext, useCallback } from 'react';
import Cookies from 'js-cookie';
import { PageContext } from '../../../contexts/ContextCreator';
import { EmitMetric } from '../../Analytics/VisibilityPixel/VisibilityPixel';
import {
    userEventStatusTypes,
    registrationSourceTypes,
} from '../../Includes/Constants/events';
import { eventPropTypesShape } from '../../Includes/eventsProps';
import {
    getUserEventStatus,
    checkShouldHideButton,
    openExternalRegistrationUrl,
} from '../helperFunctions';

import {
    EventOverButton,
    RegisterButton,
    RegistrationClosedButton,
    RegisterRegisterToJoinButton,
} from './EventRegisterButtons';

import { AttendingButton } from './AttendingButton';

import { eventRegistrationFlowType } from '../../Modals/Flows/EventRegistration/EventRegistration';
import {
    QUICK_PROFILE_STEP_NAME,
    SELECT_TIME_STEP_NAME,
    EVENT_REQUIREMENTS_INPUT_STEP_NAME,
    CONFIRM_SUCCESS_STEP_NAME,
} from '../../Modals/Flows/Steps/constants';

import './EventRegisterButtonContainer.scss';

const {
    registrationFlow,
    checkHasRequirements,
    checkIsVcfSourceType,
} = require('../../Modals/Flows/EventRegistration/constants');

const { registrationFlowAPI } = registrationFlow;

const registerButtonClickMetric = { login_trigger: 'register-event' };

const NEW_REG_COOKIE = 'R_EVENT_NEW_USER_REG';

const EventRegisterButtonContainer = (props) => {
    const {
        toggleJobProfileSnippetComplete,
        triggerFlowModal,
        jobProfileSnippet,
        pageProps: { session, headerProps },
    } = useContext(PageContext);
    const { isLiveEvent } = props;
    const { isCompleted } = jobProfileSnippet;

    // `options` is the click event when a button calls this - only the
    // post-login effect below passes { isAutoTriggered: true }.
    const triggerEventRegisterModal = useCallback(
        async (options) => {
            const { setUserAsRegistered, event } = props;
            const {
                vreProfile = {},
                vreAdditionalInfo,
                hash,
                sourceType,
                registrationUrl,
            } = event;

            const handleEventRegisterClickMetrics = () => {
                const sponsorIds = event.sponsors.map(
                    (company) => company.companyId
                );

                return EmitMetric({
                    misc_event_count: 1,
                    misc_event_type: 'event-register-click',
                    event_id: event.id,
                    company_id: sponsorIds,
                });
            };

            handleEventRegisterClickMetrics();

            if (sourceType === registrationSourceTypes.INTERNAL) {
                openExternalRegistrationUrl(registrationUrl, {
                    isAutoTriggered: options?.isAutoTriggered === true,
                });
                return;
            }

            const quickProfileRequired = !isCompleted;
            const eventReqInputStepRequired = checkHasRequirements(
                vreAdditionalInfo
            );

            const baseQuickProfile = session.name
                ? { name: session.name, education: {}, experience: {} }
                : {};

            const requiredSteps = {
                [QUICK_PROFILE_STEP_NAME]: quickProfileRequired,
                [EVENT_REQUIREMENTS_INPUT_STEP_NAME]: eventReqInputStepRequired,
            };

            const eventRequirementsInput = checkIsVcfSourceType(sourceType)
                ? {
                      ...vreProfile,
                      email: headerProps?.session?.email || '',
                  }
                : vreProfile;

            const rawPopulateProps = {
                quickProfile: baseQuickProfile,
                eventRequirementsInput,
            };

            const modalData = {
                stepPopulateProps: registrationFlow.buildDefaultRegFlowState(
                    rawPopulateProps
                ),
                generalModalProps: {
                    event,
                    setUserAsRegistered,
                    requiredSteps,
                    eventPreferredTimes: [],
                    startStep: QUICK_PROFILE_STEP_NAME,
                    toggleJobProfileSnippetComplete,
                    sourceType,
                },
            };

            if (quickProfileRequired) {
                return triggerFlowModal(eventRegistrationFlowType, modalData);
            }

            if (!quickProfileRequired && eventReqInputStepRequired) {
                modalData.generalModalProps.startStep = EVENT_REQUIREMENTS_INPUT_STEP_NAME;
                return triggerFlowModal(eventRegistrationFlowType, modalData);
            }

            if (!quickProfileRequired && !eventReqInputStepRequired) {
                const regPayload = {
                    eventHash: hash,
                    registerPayload: {},
                };

                const {
                    ok,
                    data,
                } = await registrationFlowAPI.submitEventRegistration(
                    regPayload
                );

                if (!ok) {
                    return;
                }

                if (!data.eventPreferredTimes) {
                    modalData.generalModalProps.startStep = CONFIRM_SUCCESS_STEP_NAME;
                    setUserAsRegistered();
                    return triggerFlowModal(
                        eventRegistrationFlowType,
                        modalData
                    );
                }

                modalData.generalModalProps.eventPreferredTimes =
                    data.eventPreferredTimes;
                modalData.generalModalProps.startStep = SELECT_TIME_STEP_NAME;
                modalData.generalModalProps.requiredSteps[
                    SELECT_TIME_STEP_NAME
                ] = true;

                // event has preferred times - set step to time select and trigger modal
                return triggerFlowModal(eventRegistrationFlowType, modalData);
            }
        },
        [
            props,
            session.name,
            headerProps,
            toggleJobProfileSnippetComplete,
            triggerFlowModal,
            isCompleted,
        ]
    );

    useEffect(() => {
        const { event, isUserRegistered } = props;
        const cookieValue = Cookies.get(NEW_REG_COOKIE);
        if (
            cookieValue &&
            isCompleted &&
            `${event.id}` === cookieValue &&
            !isUserRegistered
        ) {
            triggerEventRegisterModal({ isAutoTriggered: true });
            Cookies.remove(NEW_REG_COOKIE);
        }
    }, [triggerEventRegisterModal, isCompleted, props]);

    const { event, isSingleEventPage } = props;
    const { wrapAuth } = useContext(PageContext);
    const { sourceType, registrationUrl, joinUrl } = event;

    const userEventStatus = getUserEventStatus(
        event,
        props.currentTime,
        props.isUserRegistered,
        isLiveEvent
    );

    if (
        checkShouldHideButton({
            event,
            sourceType,
            registrationUrl,
            userEventStatus,
        })
    ) {
        return null;
    }

    let EventButton;
    switch (userEventStatus) {
        case userEventStatusTypes.REGISTERED_IN_PROGRESS:
            EventButton = (
                <AttendingButton
                    isSingleEventPage={isSingleEventPage}
                    url={joinUrl}
                    isLiveEvent={isLiveEvent}
                />
            );
            break;
        case userEventStatusTypes.REGISTRATION_CLOSED:
            EventButton = <RegistrationClosedButton />;
            break;
        case userEventStatusTypes.REGISTERED_STARTING_SOON:
            EventButton = (
                <AttendingButton
                    isSingleEventPage={isSingleEventPage}
                    url={joinUrl}
                    isLiveEvent={isLiveEvent}
                    isStartingSoon
                />
            );
            break;
        case userEventStatusTypes.NOT_REGISTERED_STARTING_SOON:
            EventButton = (
                <RegisterRegisterToJoinButton
                    onClick={triggerEventRegisterModal}
                />
            );
            break;
        case userEventStatusTypes.EVENT_OVER:
            EventButton = <EventOverButton />;
            break;
        case userEventStatusTypes.NOT_REGISTERED_INPROGRESS:
            EventButton = (
                <RegisterRegisterToJoinButton
                    onClick={triggerEventRegisterModal}
                />
            );
            break;
        case userEventStatusTypes.USER_REGISTERED_EVENT_OPEN:
            EventButton = (
                <AttendingButton
                    isSingleEventPage={isSingleEventPage}
                    url={joinUrl}
                    isLiveEvent={isLiveEvent}
                />
            );
            break;
        case userEventStatusTypes.USER_REGISTERED:
            EventButton = (
                <AttendingButton isSingleEventPage={isSingleEventPage} />
            );
            break;
        default:
            EventButton = (
                <RegisterButton onClick={triggerEventRegisterModal} />
            );
    }

    const eventRegistrationWrapper = () => {
        const buttonComponent = wrapAuth(
            'RegisterEvent',
            EventButton,
            registerButtonClickMetric,
            null,
            null,
            {
                name: NEW_REG_COOKIE,
                value: `${event.id}`,
            }
        );

        return buttonComponent;
    };

    return (
        <div className="component-Events-EventRegisterButtonContainer">
            {eventRegistrationWrapper()}
        </div>
    );
};

EventRegisterButtonContainer.propTypes = {
    /**
     * single event data
     */
    // eslint-disable-next-line react/require-default-props
    event: eventPropTypesShape.isRequired,
    /**
     * whether this register button is rendered from single event page or not
     */
    // eslint-disable-next-line react/require-default-props
    isSingleEventPage: PropTypes.bool.isRequired,
    /**
     * whether user is register for the event or not
     */
    // eslint-disable-next-line react/require-default-props
    isUserRegistered: PropTypes.bool.isRequired,
    /**
     * set userRegistered to true
     */
    // eslint-disable-next-line react/require-default-props
    setUserAsRegistered: PropTypes.func.isRequired,
    /**
     * current time of the application is being rendered
     */
    currentTime: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    /**
     * the event already started
     */
    isLiveEvent: PropTypes.bool,
};

EventRegisterButtonContainer.defaultProps = {
    // eslint-disable-next-line react/default-props-match-prop-types
    event: {},
    // eslint-disable-next-line react/default-props-match-prop-types
    isSingleEventPage: false,
    // eslint-disable-next-line react/default-props-match-prop-types
    isUserRegistered: false,
    currentTime: null,
    // eslint-disable-next-line react/default-props-match-prop-types
    setUserAsRegistered: () => {},
    isLiveEvent: false,
};

export default EventRegisterButtonContainer;
